import {
  AfterViewInit,
  Directive,
  ElementRef,
  HostListener,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Renderer2,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { Subject } from 'rxjs';

import { FormFieldControl } from '../components/forms/form-field/form-field.control';

@Directive({
  selector: 'input[ctrInput], textarea[ctrInput]',
  standalone: true,
  providers: [
    {
      provide: FormFieldControl,
      useExisting: InputDirective,
    },
  ],
})
export class InputDirective extends FormFieldControl<string> implements AfterViewInit, OnDestroy, OnInit {
  // ****************************************************************************
  // * Static properties                                                        *
  // ****************************************************************************

  /** Counter for generating unique IDs. */
  static nextId = 0;

  // ****************************************************************************
  // * Decorated properties                                                     *
  // ****************************************************************************

  /** Unique identifier for the input. */
  @Input() override id = `mol-input-${InputDirective.nextId++}`;

  // ****************************************************************************
  // * Public properties                                                        *
  // ****************************************************************************

  /**
   * Stream that emits whenever the state of the control changes.
   */
  override stateChanges = new Subject<void>();

  // ****************************************************************************
  // * Private properties                                                       *
  // ****************************************************************************

  /** Flag to always set label floated. */
  private alwaysFocus = false;

  /** Internal value of the control. */
  private _value?: string;

  /** Callback function to be called when the control's value changes. */
  private onChange?: (_: string) => void;

  /** Callback function to be called when the control is touched (blurred). */
  private onTouched?: () => void;

  // ****************************************************************************
  // * Getters and setters                                                      *
  // ****************************************************************************

  /**
   * Gets the value of the control.
   * @returns The current value of the control.
   */
  get value(): string {
    return this._value ?? '';
  }

  /**
   * Sets the value of the control.
   * @param val The value to set.
   */
  set value(val: string) {
    if (val !== this._value) {
      this._value = val;
      this._elementRef.nativeElement.value = val ?? '';
      this.onChange?.(val);
      this.stateChanges.next();
    }
  }

  /**
   * Checks whether the control is empty.
   * @returns True if the control is empty.
   */
  get empty(): boolean {
    return !this._elementRef.nativeElement.value;
  }

  // ****************************************************************************
  // * Constructor                                                              *
  // ****************************************************************************

  private _elementRef = inject(ElementRef<HTMLInputElement | HTMLTextAreaElement>);
  private _renderer = inject(Renderer2);
  private ngControl = inject(NgControl, { optional: true, self: true });

  constructor() {
    super();
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }
  }

  // ****************************************************************************
  // * Angular lifecycle hooks                                                  *
  // ****************************************************************************

  /** Initializes the directive and subscribes to control status changes. */
  ngOnInit(): void {
    this.ngControl?.statusChanges?.subscribe(() => {
      this._updateErrorState();
    });
  }

  /**
   * Called after the view has been initialized.
   * Adds necessary classes and sets initial states for the input element.
   */
  ngAfterViewInit(): void {
    this._renderer.addClass(this._elementRef.nativeElement, 'outline-none');
    this._renderer.addClass(this._elementRef.nativeElement, 'focus:outline-none');
    this._renderer.addClass(this._elementRef.nativeElement, 'w-full');
    if (this._elementRef.nativeElement.tagName === 'INPUT') {
      this._renderer.setStyle(this._elementRef.nativeElement, 'height', '24px');
      this.alwaysFocus =
        this.checkType('date', this._elementRef.nativeElement) ||
        this.checkType('time', this._elementRef.nativeElement);
      this.focused.set(this.alwaysFocus);
      this.stateChanges.next();
    }
    if (this._elementRef.nativeElement.tagName === 'TEXTAREA') {
      this._renderer.setStyle(this._elementRef.nativeElement, 'height', 'auto');
      this.stateChanges.next();
    }
    if (this._elementRef.nativeElement.placeholder !== '') {
      this.alwaysFocus = true;
      this.focused.set(true);
      this.stateChanges.next();
    }
  }

  /** Unsubscribes from state changes and completes the stream. */
  ngOnDestroy(): void {
    this.stateChanges.complete();
  }

  // ****************************************************************************
  // * Public methods                                                           *
  // ****************************************************************************

  /**
   * Handles the blur event on the input element.
   */
  @HostListener('blur')
  onBlur(): void {
    if (!this.alwaysFocus) {
      this.focused.set(false);
    }
    this.onTouched?.();
    this._updateErrorState();
    this.stateChanges.next();
  }

  /**
   * Handles the focus event on the input element.
   */
  @HostListener('focus')
  onFocus(): void {
    this.focused.set(true);
    this.stateChanges.next();
  }

  /**
   * Handles the input event on the input element.
   * @param value The value of the input.
   */
  @HostListener('input', ['$event.target.value'])
  onInput(value: any): void {
    this.value = value;
  }

  /**
   * Sets the describedBy attribute with the provided IDs.
   * @param ids Array of IDs to set in the aria-describedby attribute.
   */
  override setDescribedByIds(ids: string[]): void {
    const controlElement = this._elementRef.nativeElement;
    controlElement.setAttribute('aria-describedby', ids.join(' '));
  }

  /**
   * Handles click events on the container element.
   */
  onContainerClick(): void {
    if (!this.focused()) {
      this._elementRef.nativeElement.focus();
    }
  }

  /**
   * Writes a new value to the input element.
   * @param value The value to write.
   */
  override writeValue(value: string): void {
    this._value = value;
    this._elementRef.nativeElement.value = value ?? '';
    this.stateChanges.next();
  }

  /**
   * Registers a function to be called when the control value changes.
   * @param fn The callback function.
   */
  override registerOnChange(fn: (_: string) => void): void {
    this.onChange = fn;
  }

  /**
   * Registers a function to be called when the control is touched.
   * @param fn The callback function.
   */
  override registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  /**
   * Sets the disabled state of the input element.
   * @param isDisabled True to disable the control.
   */
  override setDisabledState?(isDisabled: boolean): void {
    this._elementRef.nativeElement.disabled = isDisabled;
    this.isDisabled.set(isDisabled);
    this.stateChanges.next();
  }

  // ****************************************************************************
  // * Private methods                                                          *
  // ****************************************************************************

  /**
   * Updates the error state based on the control's validation status.
   */
  private _updateErrorState(): void {
    const oldState = this.errorState();
    const control = this.ngControl ? this.ngControl.control : null;

    const newState = !!(control && control.invalid && (control.touched || control.dirty));

    if (newState !== oldState) {
      this.errorState.set(newState);
      this.stateChanges.next();
    }
  }

  /**
   * Checks the input element type.
   * @param type The type to check.
   * @param element The input element.
   * @returns True if the element type matches.
   */
  private checkType(type: string, element: HTMLInputElement | HTMLTextAreaElement): boolean {
    return element.type === type;
  }
}
